<script>
  import { Modal, Label, Select, Textarea, Button } from "flowbite-svelte";
  import { customerSupport } from "../service/http_calls";
  import { support_modal_open } from "../service/store";

  // Opened from the "Support" link in the footer (see Footer.svelte). This
  // component only renders the modal; there is no floating launcher.
  let category = "";
  let sub_category = "";
  let message = "";
  let submitting = false;
  let submitted = false;
  let error = "";

  const categories = {
    "Technical Issue": ["Draft", "Review", "Qualify", "Research", "Other"],
    Billing: ["Invoice Inquiry", "Refund Request", "Payment Method", "Other"],
    "General Inquiry": ["Product Information", "Feedback", "Other"],
  };

  $: category_items = Object.keys(categories).map((c) => ({ value: c, name: c }));
  $: sub_category_items = (categories[category] || []).map((c) => ({ value: c, name: c }));
  // Changing category clears any feature picked under the previous one.
  $: if (!sub_category_items.some((i) => i.value === sub_category)) sub_category = "";

  function resetForm() {
    category = "";
    sub_category = "";
    message = "";
  }

  async function handleSubmit() {
    if (submitting) return;
    submitting = true;
    error = "";
    try {
      await customerSupport({ category, sub_category, message });
      submitted = true;
      resetForm();
    } catch (e) {
      error = "Something went wrong sending your message. Please try again.";
    } finally {
      submitting = false;
    }
  }

  // Closing (cancel, X, backdrop) discards whatever was typed so the form is
  // blank next time it opens.
  function close() {
    support_modal_open.set(false);
    submitted = false;
    error = "";
    resetForm();
  }
</script>

<Modal
  bind:open={$support_modal_open}
  on:close={close}
  size="sm"
  class="divide-y-0"
  classHeader="flex justify-between items-center px-4 pt-4 pb-0 md:px-5 md:pt-5 md:pb-0 rounded-t-lg"
>
  <svelte:fragment slot="header">
    <h3 class="text-lg font-medium text-gray-900 dark:text-white">Contact support</h3>
  </svelte:fragment>

  {#if !submitted}
    <form class="flex flex-col gap-4" on:submit|preventDefault={handleSubmit}>
      <Label class="space-y-2">
        <span>Category</span>
        <Select
          items={category_items}
          bind:value={category}
          placeholder="Select a category"
          class="sc-field-focus"
          required
        />
      </Label>

      {#if category}
        <!-- keyed so the feature list is rebuilt from scratch when the category changes -->
        {#key category}
          <Label class="space-y-2">
            <span>Feature</span>
            <Select
              items={sub_category_items}
              bind:value={sub_category}
              placeholder="Select a feature"
              class="sc-field-focus"
              required
            />
          </Label>
        {/key}
      {/if}

      <Label class="space-y-2">
        <span>Message</span>
        <Textarea bind:value={message} rows="5" class="sc-field-focus" required />
      </Label>

      {#if error}
        <p class="text-sm text-red-600 dark:text-red-400">{error}</p>
      {/if}

      <div class="flex items-center justify-between mt-2">
        <Button
          type="button"
          color="alternative"
          pill
          class="border-[#d8d8da] dark:border-gray-600"
          on:click={close}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          pill
          disabled={submitting}
          class="!bg-[#050c3b] dark:!bg-white !text-white dark:!text-[#050c3b] !border-0 hover:!bg-[#1a2070] dark:hover:!bg-gray-200"
        >
          {submitting ? "Sending…" : "Send"}
        </Button>
      </div>
    </form>
  {:else}
    <div class="flex flex-col gap-4">
      <p class="text-gray-700 dark:text-gray-300">
        Thanks, we've received your message and will get back to you as soon as possible.
      </p>
      <div class="flex justify-end">
        <Button
          pill
          class="!bg-[#050c3b] dark:!bg-white !text-white dark:!text-[#050c3b] !border-0 hover:!bg-[#1a2070] dark:hover:!bg-gray-200"
          on:click={close}
        >
          Close
        </Button>
      </div>
    </div>
  {/if}
</Modal>
